/**
 * The scheduler may be launched from anywhere in the app
 * as long as it is provided with a Bundle that has
 * resDays and resResources
 *
 */
import * as React from 'react';
import {createRef} from 'react';
import * as FocusTrap from "focus-trap-react";
import {observer} from "mobx-react";
import {action, computed, observable} from "mobx";
import './Scheduler.pcss';
import {UIState} from "data/UIState";
import {AvailableSlots} from "components/reservations/AvailableSlots";
import {
  FAILURE_REASON_SPOT_TAKEN,
  ResData,
} from "components/reservations/ResData";
import {SchedulerOverview} from "components/reservations/SchedulerOverview";
import {AvailableDays} from "components/reservations/AvailableDays";
import {IResDay} from "shared/ResDay";
import {IReservation} from "shared/IReservation";
import {ScheduledSlot} from "components/reservations/ScheduledSlot";
import {
  ScheduleMessagePanel,
} from "components/reservations/SchedulerMessagePanel";
import {DateTime} from "luxon";
import {DateTimeFormat, DateTimeHelpers} from "shared/DateTimeHelpers";
import {StringHelpers} from "shared/StringHelpers";

const SchedulerPanel = {
  MAIN:'MAIN',
  DAYS:'DAYS',
  SLOTS: 'SLOTS',
  SCHEDULED: 'SCHEDULED',
}

@observer
export class Scheduler extends React.Component {

  @action handleClose = () => {
    UIState.closeScheduler();
  }

  render() {
    if (!UIState.resData) {
      return null;
    } else {
      return <_Scheduler resData={UIState.resData} onClose={this.handleClose}/>
    }
  }
}

interface Props {
  resData:ResData;
  onClose:()=>void;
}

@observer
class _Scheduler extends React.Component<Props> {
  schedulerRef = createRef<any>();
  messageRef = createRef<any>();

  @observable showMessage = false;    // Opens the message panel
  @observable messagePanelMessage:any = "";    // Generic feedback message

  @observable startingPanel = SchedulerPanel.MAIN;
  @observable panel = SchedulerPanel.MAIN;
  @observable reservation:IReservation|null = null;
  @observable reservationToReschedule:IReservation|null = null;
  @observable agreedToTerms = false;

  constructor(props:Props) {
    super(props);
    if(this.slotsAlreadyReserved.length>0) {
      this.startingPanel = SchedulerPanel.MAIN;
    } else if(props.resData.isMultiDay && this.props.resData.resDaysOpenForRegistration.length>1) {
      this.startingPanel = SchedulerPanel.DAYS;
    } else {
      this.props.resData.goToDay(this.props.resData.resDaysOpenForRegistration[0]);
      this.startingPanel = SchedulerPanel.SLOTS;
    }
    this.panel = this.startingPanel;
  }

  @computed get slotsAlreadyReserved():DateTime[] {
    const slotsAlreadyReserved:DateTime[] = [];
    if(this.props.resData.reservations) {
      for (let res of this.props.resData.reservations) {
        const resTime = DateTimeHelpers.parseDateTime(res.time);
        if(null!==resTime) {
          slotsAlreadyReserved.push(resTime);
        }
      }
    }
    return slotsAlreadyReserved;
  }

  @action onBack = () => {
    if(this.panel===SchedulerPanel.DAYS) {
      this.reservationToReschedule = null;
      this.panel = SchedulerPanel.MAIN;
    } else if(this.panel===SchedulerPanel.SLOTS) {
      if(this.props.resData.resDaysOpenForRegistration.length>1) {
        this.panel = SchedulerPanel.DAYS;
      } else {
        this.panel = SchedulerPanel.MAIN;
      }
    } else {
      this.reservationToReschedule = null;
      this.panel = SchedulerPanel.MAIN;
    }
  }

  @action handleClickScheduleASession = () => {
    if(this.props.resData.resDaysOpenForRegistration.length>1) {
      this.panel = SchedulerPanel.DAYS;
    } else {
      this.props.resData.goToDay(this.props.resData.resDaysOpenForRegistration[0]);
      this.panel = SchedulerPanel.SLOTS;
    }
  }

  @observable reservationToCancel:IReservation|null;

  @action handleCancelReservation = (reservation:IReservation) => {
    this.reservationToCancel = reservation;
    this.openMessage();
  }

  @action cancelReservation = (reservation:IReservation) => new Promise((resolve, reject)=>{
    this.props.resData.cancelReservation(reservation)
      .then(()=>{
        this.panel = SchedulerPanel.MAIN;
        this.closeMessage();
        resolve(true);
      })
      .catch((reason)=>{
        this.closeMessage();
        console.log(reason);
        this.messagePanelMessage = <p>Something went wrong when we tried to cancel. Please try again. If that does not work, reload your browser and try again.</p>
        this.openMessage();
        reject();
      });
  });

  @observable private clickedSlot:DateTime|null;

  @action handleClickSlot = (slot:DateTime) => new Promise<any>(action((resolve, reject) => {
    this.clickedSlot = slot;
    this.agreedToTerms = false;
    this.openMessage();
    resolve(true);
  }));

  @action openMessage = () => {
    this.showMessage = true;
    this.schedulerRef.current.focusTrap.deactivate();
    // activate the message focus trap
    setTimeout(()=>{
      this.messageRef.current.focusTrap.activate();
    }, 100);
  }

  @action closeMessage = () => {
    this.messageRef.current.focusTrap.deactivate();
    this.schedulerRef.current.focusTrap.activate();
    this.showMessage = false;

    // specific objects that trigger specific modals
    this.clickedSlot = null;
    this.reservationToCancel = null;
    this.messagePanelMessage = "";
  }

  @action reserveClickedSlot = (slot:DateTime) => new Promise((resolve, reject) => {

    this.closeMessage();

    let day:IResDay|null = this.props.resData.currentDay;
    if (!day) {
      day = this.props.resData.dayForSlotDateTime(slot);
    }

    if (!day) {
      return reject("Something went wrong! Please reload your browser and try again.");
    }

    for(let slotAlreadyReserved of this.slotsAlreadyReserved) {
      if(slot.hasSame(slotAlreadyReserved, 'minute')) {
        return reject("You've already reserved this slot.");
      }
    }
/// solutionId
    this.props.resData.reserveSlot(day, slot, this.reservationToReschedule ? this.reservationToReschedule : undefined)
      .then(action(() => {
        resolve(true);
        this.props.onClose();
        // this.panel = SchedulerPanel.MAIN;
      }))
      .catch((reason) => {
        if(FAILURE_REASON_SPOT_TAKEN===reason) {
          if(this.props.resData.noSlotsAvailable) {
            this.messagePanelMessage = <p>{`${slot.toLocaleString(DateTime.TIME_SIMPLE)} was just taken by someone else. 
              Unfortunately, that was the last available ${this.props.resData.resOptions.uiWordSlot}, 
              and you won’t be able to ${this.props.resData.resOptions.uiWordReserve} for that time.`}
            </p>
          } else {
            this.messagePanelMessage = <p>Sorry, {slot.toLocaleString(DateTime.TIME_SIMPLE)} was just taken.<br/>Choose another time.</p>
          }
        } else {
          console.log(reason);
          this.messagePanelMessage = <p>Something went wrong when we tried to book your time. Please try again. If that does not work, reload your browser and try again.</p>
        }
        this.openMessage();
        reject(reason);
      })
  });

  @action handleClickResDay = (resDay:IResDay) => {
      this.props.resData.goToDay(resDay);
      this.panel = SchedulerPanel.SLOTS;
  }

  @action handleClickReservation = (reservation:IReservation) => {
    this.reservation = reservation;
    this.reservationToReschedule = null;
    this.panel = SchedulerPanel.SCHEDULED;
  }

  @action handleReschedule = (reservation:IReservation) => {
    this.reservationToReschedule = reservation;
    if(this.props.resData.resDaysOpenForRegistration.length>1) {
      this.panel = SchedulerPanel.DAYS;
    } else {
      this.props.resData.goToDay(this.props.resData.resDaysOpenForRegistration[0]);
      this.panel = SchedulerPanel.SLOTS;
    }
  }

  render() {
      const {resData, onClose} = this.props;
      return (

        <div className={"scheduler-modal"}>
          <div className={"scheduler-widget-container "}>
            <FocusTrap
              ref={this.schedulerRef}
              focusTrapOptions={
              {
                allowOutsideClick: false,
                clickOutsideDeactivates: false,
                onDeactivate: () => {
                  // console.log("deactivate scheduler");
                },
                preventScroll:true,

              }
            }>
              <div className="scheduler-scroll-wrapper" role="alert" onClick={(e) => {
                e.stopPropagation();
              }}>
                <div className={"scheduler-nav"}>
                  <div>
                    {this.panel!==this.startingPanel &&
                    <button onClick={this.onBack}>
                      <span aria-hidden="true" className={"material-icons"}>arrow_back_ios</span>
                      <span className="visuallyhidden">Back</span>
                    </button>
                    }
                  </div>
                  <div>
                    {this.panel!==this.startingPanel &&
                      <h1>{resData.fullName}</h1>
                    }
                  </div>
                  <div>
                    <button onClick={onClose}>
                        <span aria-hidden="true" className={"material-icons"}>close</span>
                        <span className="visuallyhidden">Close The Scheduler</span>
                    </button>
                  </div>
                </div>

                <div className={"scheduler-scroll-content"}>
                  {this.panel===this.startingPanel &&
                    <div className={"scheduler-starting-header"}>
                      <h1>{resData.fullName}</h1>
                      <p>{resData.what}</p>
                    </div>
                  }

                  {this.panel===SchedulerPanel.MAIN &&
                    <SchedulerOverview
                      resDays={resData.resDaysOpenForRegistration}
                      reservations={resData.futureReservations}
                      onClickScheduleASession={this.handleClickScheduleASession}
                      onClickResDay={this.handleClickResDay}
                      onClickReservation={this.handleClickReservation}
                      onClickSlot={this.handleClickSlot}
                      onCancelReservation={this.handleCancelReservation}
                      onReschedule={this.handleReschedule}

                      sessionLength={resData.resOptions.slotLength}
                      bundleName={resData.fullName}

                      uiPhraseSlotDescription={resData.uiPhraseSlotDescription}
                      uiWordEventType={resData.resOptions.uiWordEventType}
                      uiWordReservation={resData.resOptions.uiWordReservation}
                      uiWordSlot={resData.resOptions.uiWordSlot}
                      uiWordReserve={resData.resOptions.uiWordReserve}

                      isMultiDay={resData.isMultiDay}
                      isMultiTime={resData.isMultiTime}
                      noSlotsAvailable={resData.noSlotsAvailable}
                      timezone={resData.resOptions.timezone}
                    />
                  }

                  {this.panel===SchedulerPanel.DAYS &&
                    <AvailableDays
                      resDays={resData.resDaysOpenForRegistration}
                      onClickResDay={this.handleClickResDay}
                      isRescheduling={null!=this.reservationToReschedule}
                      uiWordSlot={resData.resOptions.uiWordSlot}
                      timezone={resData.resOptions.timezone}
                    />
                  }
                  {this.panel===SchedulerPanel.SLOTS &&
                    <AvailableSlots
                      onClickSlot={this.handleClickSlot}
                      hasNext={null!=resData.nextDay}
                      hasPrevious={null!=resData.previousDay}
                      onGoPrevious={resData.goToPreviousDay}
                      onGoNext={resData.goToNextDay}
                      resDay={resData.currentDay}
                      reservationToReschedule={this.reservation}
                      slotsAlreadyReserved={this.slotsAlreadyReserved}
                      timezone={resData.resOptions.timezone}
                      uiWordSlot={resData.resOptions.uiWordSlot}
                    />
                  }
                  {this.panel===SchedulerPanel.SCHEDULED && this.reservation &&
                    <ScheduledSlot
                      bundleName={resData.fullName}
                      onCancelReservation={this.handleCancelReservation}
                      onReschedule={this.handleReschedule}
                      reservation={this.reservation}
                      uiWordReservation={resData.resOptions.uiWordReservation}
                      uiWordSlot={resData.resOptions.uiWordSlot}
                      timezone={resData.resOptions.timezone}
                    />
                  }
                </div>
              </div>
            </FocusTrap>

            {this.showMessage &&
              <FocusTrap
                ref={this.messageRef}
                focusTrapOptions={
                {
                  allowOutsideClick: false,
                  clickOutsideDeactivates: false,
                  onDeactivate:() => {
                    // console.log("deactivate scheduler message");
                  },
                  preventScroll:true,
                }
              }>
                <div>
                  {this.clickedSlot &&
                    <ScheduleMessagePanel
                      buttonLabelOK={resData.resOptions.uiWordReserve}
                      buttonLabelCancel={"Cancel"}
                      onCancelAction={this.closeMessage}
                      onOKAction={()=>{ if(this.clickedSlot) { this.reserveClickedSlot(this.clickedSlot);}}}
                      disableOk={ !this.agreedToTerms && resData.hasTerms}
                      okDisabledWarning={"You must agree to the terms."}
                      content={<>
                        <span className={"visuallyhidden"}>Cancel or schedule {resData.uiPhraseSlotDescription} for {DateTimeHelpers.format(this.clickedSlot, DateTimeFormat.SLOT_UI, resData.resOptions.timezone)}.</span>

                        <p aria-hidden={true}>
                          {resData.uiPhraseSlotDescription}<br/>
                          {DateTimeHelpers.format(this.clickedSlot, DateTimeFormat.SLOT_UI, resData.resOptions.timezone)}
                        </p>

                        {resData.hasTerms &&
                          <div className={"scheduler-agreed"}>
                            <div className={"scheduler-agreed-terms"}><div dangerouslySetInnerHTML={{__html:resData.resOptions.terms}}></div></div>
                            <div className={"scheduler-agreed-cbx"}>
                              <div role={"checkbox"}
                                   className={`accept${this.agreedToTerms ? ' invalid' : ''}`}
                                   aria-checked={this.agreedToTerms} tabIndex={0} onClick={action(()=>{
                                     this.agreedToTerms = !this.agreedToTerms;
                              })}>
                                <a className="checkbox noselect">
                                  <i aria-hidden={true} className="material-icons">{this.agreedToTerms ? 'check_box' : 'check_box_outline_blank'}</i>
                                  I have read and agree to these terms
                                </a>
                              </div>
                            </div>
                          </div>
                        }
                      </>
                      }
                    />
                  }

                  {this.reservationToCancel &&
                    <ScheduleMessagePanel
                      content={<>
                        <h2>Are you sure you want to cancel?</h2>
                        <p>
                          {resData.what}<br/>
                          {DateTimeHelpers.format(this.reservationToCancel.time, DateTimeFormat.SLOT_UI, resData.resOptions.timezone)}
                        </p>
                      </>}
                      onOKAction={()=>{
                        if(this.reservationToCancel) {
                          this.cancelReservation(this.reservationToCancel);
                        }
                      }}
                      onCancelAction={this.closeMessage}
                      buttonLabelCancel={"Not Now"}
                      buttonLabelOK={"Confirm Cancellation"}
                    />
                  }

                  {this.messagePanelMessage &&
                    <ScheduleMessagePanel
                      content={this.messagePanelMessage}
                      onOKAction={this.closeMessage}
                    />
                  }

                </div>
              </FocusTrap>
            }

          </div>
        </div>
      );
  }
}


